import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Col, Form, Input, Row, Select, App, Typography } from 'antd';
import { DeleteOutlined, LeftOutlined } from '@ant-design/icons';
import { getRegionById, createRegion, updateRegion, listStates, addRegionState, deleteRegionState, checkRegionExists} from '../../services/regions';
import { List } from 'antd';
import { Popconfirm } from 'antd';
import usePermissions from '../../hooks/usePermissions';

const { Title } = Typography;

const { useApp } = App;

const RegionUpsertPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  
  const navigate = useNavigate();
  const { message } = useApp();
  const [form] = Form.useForm();

  const { canAny } = usePermissions();

  const [loading, setLoading] = useState(false);
  const [states, setStates] = useState([]);
  const [regionStates, setRegionStates] = useState([]);
  const availableStates = states.filter(
    state => !regionStates.some(rs => rs.state_id === Number(state.id))
  );


  useEffect(() => {
    listStates()
      .then((statesList) => {
        setStates(statesList);
      })
      .catch(() => {
        message.error('No se pudieron cargar los estados');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isEdit || !id) return;

    setLoading(true);
    getRegionById(id)
      .then((region) => {
        const normalizedRegionStates = region.regionStates.map(rs => ({
        state_id: rs.state.id,
        state: rs.state,
      }));
      setRegionStates(normalizedRegionStates);
        form.setFieldsValue({
          email: region.email
            ? region.email
                .split(',')
                .map((e) => e.trim())
                .filter(Boolean)
            : [],
          description: region.description?.trim(),
        });
      })
      .catch(() => {
        message.error('No se pudo cargar la región');
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit, form]);

  /**
   * Función que se ejecuta cuando se envía el formulario
   */
  const onSubmit = async (values) => {
    try {
      setLoading(true);

      const exists = await checkRegionExists(values.description.trim(),id);
      if(exists === true){
        message.warning('Ya existe una región con la descripción proporcionada');
        return;
      }

      if (isEdit && id) {
        const updateData = {
          description: values.description,
          states: values.regionStates,
        };


        await updateRegion(id, updateData);
        message.success('Región actualizada correctamente');
      } else {
        const createData = {
          description: values.description,
          states: values.regionStates,
        };

        const response = await createRegion(createData);
        message.success('Región agregada.');
        navigate(`/regions/${response.id}/edit`, { replace: true });
      }
    } catch (err) {
      // Extraer datos del error
      const errorData = err?.response?.data || err?.data || {};
      const errorObj = errorData?.error || {};
      const status = errorObj?.status || err?.response?.status || 500;
      const errorCode = errorObj?.code;
      const errorMessage = errorObj?.message || errorData?.message || err?.message || 'Error al guardar el usuario';
      
      // Si es un error de validación (422), mostrar errores en los campos del formulario
      if (status === 422 && (errorCode === 'VALIDATION_ERROR' || errorObj?.details?.issues)) {
        const issues = errorObj?.details?.issues || errorObj?.issues || [];
        
        console.log('Issues encontrados:', issues);
        
        if (issues.length > 0) {
          // Limpiar errores previos
          form.setFields([]);
          
          const fieldErrors = issues
            .filter((issue) => issue.path && issue.message) 
            .map((issue) => {
              let fieldPath = issue.path;
              
              if (typeof fieldPath === 'string') {
                fieldPath = [fieldPath];
              } else if (!Array.isArray(fieldPath)) {
                fieldPath = [String(fieldPath)];
              }
              
              console.log('Mapeando error para campo:', fieldPath, 'Mensaje:', issue.message);
              
              return {
                name: fieldPath,
                errors: [issue.message || 'Error de validación'],
              };
            });
          
          console.log('Field errors a establecer:', fieldErrors);
          
          if (fieldErrors.length > 0) {
            form.setFields(fieldErrors);
            console.log('Errores establecidos en el formulario');
          }
          
          message.error(errorMessage || 'Por favor, corrige los errores en el formulario');
        } else {
          message.error(errorMessage);
        }
      } else {
        message.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddState = async (stateId: number) => {
    try {
      if (!id) return;
      await addRegionState(id, stateId);

      const state = states.find(s => Number(s.id) === stateId);

      setRegionStates(prev => [
        ...prev,
        { state_id: stateId, state },
      ]);
      form.setFieldValue('states', undefined);
    } catch (error) {
      message.error('No se pudo agregar el estado');
    }
  };

  const handleRemoveState = async (stateId: number) => {
    try {
      
      if (!id) return;
      if(canAny(['regions.create', 'regions.update'])){
        await deleteRegionState(id, stateId);
        const removed = regionStates.find(rs => rs.state_id === stateId);
        setRegionStates(prev =>
          prev.filter(rs => rs.state_id !== stateId)
        );
        if (!availableStates.some(s => s.id === stateId)) {
          setStates(prev =>  [...prev, removed.state].sort((a, b) =>
            a.description.localeCompare(b.description)
          ));
        }
      }else {
        message.warning('No tienes permisos para realizar esta acción')
      }
    } catch (error) {
      message.error('No se pudo eliminar el estado');
    }
  };

  const handleBack = () => {
    navigate('/regions/all');
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Button 
            type="text" 
            icon={<LeftOutlined style={{ fontSize: '24px', strokeWidth: 4 }} />} 
            style={{ 
              padding: 0,
              color: '#BA955E',
              fontSize: '24px',
              fontWeight: '700',
              height: 'auto'
            }}
            onClick={handleBack}
          />
        <Title level={3} style={{ marginBottom: 0 }}>
          {isEdit ? 'Editar región' : 'Agregar región'}
        </Title>
      </div>
      <Card loading={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onSubmit}
          autoComplete="off"
        >
          <Row gutter={16}>
            <Col xs={24} md={24}>
              <Form.Item
                label="Región"
                name="description"
                rules={[
                  { required: true, message: 'La región es requerida' },
                ]}
              >
                <Input placeholder="Región" allowClear />
              </Form.Item>
            </Col>
          </Row>
          {canAny(['regions.create', 'regions.update']) && (<Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Estados"
                name="states"
                rules={[{ required: false, message: 'Debes seleccionar un estado' }]}
              >
                <Select
                  placeholder="Selecciona un estado"
                  options={availableStates.map((state) => ({
                    value: Number(state.id),
                    label: state.description,
                  }))}
                  showSearch
                  onChange={handleAddState}
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    option?.label?.toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
          </Row>)}
          <Row gutter={16}>
            <Col xs={24} md={24}>
              <List
                style={{ marginTop: 16 }}
                bordered
                dataSource={regionStates}
                locale={{ emptyText: 'Sin estados asignados' }}
                renderItem={(item) => (
                  <List.Item
                    actions={[
                      <Popconfirm
                        key="delete"
                        title="¿Eliminar estado?"
                        onConfirm={() => handleRemoveState(item.state?.id)}
                      >
                        <DeleteOutlined style={{ color: '#ff4d4f', cursor: 'pointer' }} />
                      </Popconfirm>,
                      // <a onClick={() => handleRemoveState(item.state_id)}>
                      //   eliminar
                      // </a>,
                    ]}
                  >
                    {item.state.description}
                  </List.Item>
                )}
              />
            </Col>
          </Row>
          <Row justify="end" gutter={12} style={{ marginTop: 24 }}>
            <Col>
              <Button onClick={() => navigate(-1)} disabled={loading}>
                Cancelar
              </Button>
            </Col>
            {canAny(['regions.update', 'regions.create']) && (<Col>
              <Button type="primary" htmlType="submit" loading={loading}>
                {isEdit ? 'Guardar cambios' : 'Guardar'}
              </Button>
            </Col>)}
          </Row>
        </Form>
      </Card>
    </div>
  );
};

export default RegionUpsertPage;

